// Release runner — run config modal. Pick an env, select flows (one quick-select
// chip per tag adds that group; hand-pick allowed), enter the login creds
// (held only for this request — never stored), confirm prod, launch.
import { useMemo, useState } from "react";
import api from "../../api";
import Icon from "./icons";
import { Modal, apiErrorMessage } from "./shared";

const APP_LABEL = {
  portal: "Portal (PX)",
  applicant: "Applicant (AX)",
  crm: "CRM (LX)"
};

export default function ReleaseTestModal({
  open,
  onClose,
  flows,
  meta,
  onLaunched
}) {
  const envs = useMemo(() => (meta && meta.envs ? meta.envs : []), [meta]);
  const runnableEnvs = useMemo(() => envs.filter((e) => e.runnable), [envs]);

  const [env, setEnv] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [releaseLabel, setReleaseLabel] = useState("");
  const [confirmProd, setConfirmProd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Only published flows are runnable — the page passes the published list, but
  // we re-guard here so a pending import can never slip into a release run (the
  // server's scheduler drops them anyway, yielding "no runnable flows").
  const activeFlows = useMemo(
    () =>
      (flows || []).filter(
        (f) =>
          f.enabled && !f.archived && (f.status ?? "published") === "published"
      ),
    [flows]
  );
  const flowsByApp = useMemo(() => {
    const m = new Map();
    for (const f of activeFlows) {
      const arr = m.get(f.appKey) || [];
      arr.push(f);
      m.set(f.appKey, arr);
    }
    return m;
  }, [activeFlows]);

  // Search filters the VISIBLE list (by flow name or tag); the quick-select
  // chips still act on the full set. Empty groups are dropped so the list stays
  // tight when filtered.
  const filteredByApp = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return flowsByApp;
    const m = new Map();
    for (const [app, fs] of flowsByApp) {
      const hit = fs.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          (f.tags || []).some((t) => t.toLowerCase().includes(q))
      );
      if (hit.length) m.set(app, hit);
    }
    return m;
  }, [flowsByApp, search]);

  // One quick-select chip per distinct tag across the runnable flows (case-
  // insensitive; first display casing wins). smoke/regression pinned first.
  const tagChips = useMemo(() => {
    const m = new Map();
    for (const f of activeFlows) {
      for (const raw of f.tags || []) {
        const key = String(raw).toLowerCase();
        const cur = m.get(key);
        if (cur) cur.count += 1;
        else m.set(key, { key, label: raw, count: 1 });
      }
    }
    const PINNED = ["smoke", "regression"];
    return [...m.values()].sort((a, b) => {
      const pa = PINNED.indexOf(a.key);
      const pb = PINNED.indexOf(b.key);
      if (pa !== -1 || pb !== -1) {
        if (pa === -1) return 1;
        if (pb === -1) return -1;
        return pa - pb;
      }
      return a.label.localeCompare(b.label);
    });
  }, [activeFlows]);

  const envMeta = envs.find((e) => e.id === env);
  const needsConfirm = !!(envMeta && envMeta.requireConfirm);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Add every flow carrying this tag to the current selection (case-insensitive;
  // additive so multiple group chips stack — All/None reset the whole set).
  const selectByTag = (tag) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const needle = String(tag).toLowerCase();
      for (const f of activeFlows) {
        if ((f.tags || []).some((t) => t.toLowerCase() === needle)) next.add(f.id);
      }
      return next;
    });
  const selectAll = () => setSelected(new Set(activeFlows.map((f) => f.id)));
  const clearAll = () => setSelected(new Set());

  const valid =
    !!env && selected.size > 0 && (!needsConfirm || confirmProd) && !submitting;

  const launch = async () => {
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.bssReleaseTrigger({
        env,
        flowIds: [...selected],
        releaseLabel: releaseLabel.trim() || undefined,
        creds: { username, password },
        // Server re-checks this for envs with requireConfirm (e.g. prod).
        confirm: needsConfirm ? confirmProd : true
      });
      setResult(res.data);
      onLaunched && onLaunched(res.data);
    } catch (err) {
      setError(apiErrorMessage(err) || "Could not start the release run");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setSelected(new Set());
    setConfirmProd(false);
    setSearch("");
  };

  const plural = selected.size === 1 ? "" : "s";
  const runLabel = submitting
    ? "Starting…"
    : `Run ${selected.size} flow${plural}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Run release test"
      width={900}
      height={result ? undefined : "80vh"}
      footer={
        result ? (
          <button type="button" className="bss-btn" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="bss-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="bss-btn bss-btn--primary"
              disabled={!valid}
              onClick={launch}
            >
              {runLabel}
            </button>
          </>
        )
      }
    >
      {result ? (
        <div className="qa-rt-result">
          <p>
            <Icon.Check size={14} /> Scheduled{" "}
            <strong>{result.releaseKey}</strong>.
          </p>
          <ul>
            {(result.scheduled || []).map((s) => (
              <li key={s.app}>
                {APP_LABEL[s.app] || s.app}: {s.flows} flow
                {s.flows === 1 ? "" : "s"} queued
              </li>
            ))}
          </ul>
          {(result.skipped || []).length > 0 && (
            <div className="qa-rt-skipped">
              <strong>Skipped:</strong>
              <ul>
                {result.skipped.map((s) => (
                  <li key={s.app}>
                    {APP_LABEL[s.app] || s.app}: {s.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <a
            className="bss-link"
            href={`/bss/release-tests/${encodeURIComponent(result.releaseKey)}`}
          >
            View report →
          </a>
          <button
            type="button"
            className="bss-btn"
            style={{ marginTop: 10 }}
            onClick={reset}
          >
            Run another
          </button>
        </div>
      ) : (
        <div className="qa-rt-grid">
          {/* LEFT — run config (always visible, doesn't scroll with the list) */}
          <div className="qa-rt-config">
            <label className="qa-rt-label">Environment</label>
            <select
              className="bss-input"
              value={env}
              onChange={(e) => setEnv(e.target.value)}
            >
              <option value="">Select an environment…</option>
              {envs.map((e) => (
                <option key={e.id} value={e.id} disabled={!e.runnable}>
                  {e.label}
                  {e.kind === "production" ? " — PROD" : ""}
                  {!e.runnable ? " (no URLs configured)" : ""}
                </option>
              ))}
            </select>
            {runnableEnvs.length === 0 && (
              <p className="qa-rt-hint qa-rt-warn">
                No environments have deployed URLs configured
                (RELEASE_ENV_TARGETS).
              </p>
            )}

            <label className="qa-rt-label">Portal (PX) login</label>
            <div className="qa-rt-creds">
              <input
                className="bss-input"
                placeholder="username / email"
                autoComplete="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <input
                className="bss-input"
                type="password"
                placeholder="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <p className="qa-rt-hint">
              Portal&rsquo;s username / password — used to log in,{" "}
              <strong>never stored</strong>. Leave blank for public/no-login
              flows (CRM). Applicant (AX) signs in with{" "}
              <strong>phone + OTP</strong>, which the runner can&rsquo;t
              automate yet — its login flows will block at sign-in.
            </p>

            <label className="qa-rt-label">Release label (optional)</label>
            <input
              className="bss-input"
              placeholder="e.g. v2026.06.21 — defaults to a timestamp"
              value={releaseLabel}
              onChange={(e) => setReleaseLabel(e.target.value)}
            />

            {needsConfirm && (
              <label className="qa-rt-confirm">
                <input
                  type="checkbox"
                  checked={confirmProd}
                  onChange={(e) => setConfirmProd(e.target.checked)}
                />
                <span>
                  I understand this runs against{" "}
                  <strong>{envMeta.label}</strong>. Flows tagged{" "}
                  <code>{(envMeta.denyTags || []).join(", ") || "—"}</code> are
                  blocked automatically.
                </span>
              </label>
            )}

            {error && <p className="qa-rt-hint qa-rt-warn">{error}</p>}
          </div>

          {/* RIGHT — searchable, grouped, scrollable flow list (scales to many) */}
          <div className="qa-rt-flowpane">
            <div className="qa-rt-row">
              <label className="qa-rt-label">
                Flows ({selected.size} selected)
              </label>
              <div className="qa-rt-chips">
                {tagChips.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className="qa-chip"
                    onClick={() => selectByTag(c.label)}
                    title={`Add the ${c.count} flow(s) tagged "${c.label}"`}
                  >
                    {c.label} <span className="qa-chip-count">{c.count}</span>
                  </button>
                ))}
                <button type="button" className="qa-chip" onClick={selectAll}>
                  All
                </button>
                <button type="button" className="qa-chip" onClick={clearAll}>
                  None
                </button>
              </div>
            </div>
            <div className="qa-rt-search">
              <Icon.Search size={14} />
              <input
                className="bss-input"
                placeholder="Search flows by name or tag…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="qa-rt-flows">
              {activeFlows.length === 0 && (
                <p className="qa-rt-empty">No flows yet — author some first.</p>
              )}
              {activeFlows.length > 0 && filteredByApp.size === 0 && (
                <p className="qa-rt-empty">No flows match “{search.trim()}”.</p>
              )}
              {[...filteredByApp.entries()].map(([app, fs]) => (
                <div key={app} className="qa-rt-app">
                  <div className="qa-rt-app-hd">
                    <span>{APP_LABEL[app] || app}</span>
                    <span className="qa-rt-app-count">{fs.length}</span>
                  </div>
                  {fs.map((f) => (
                    <label
                      key={f.id}
                      className={`qa-rt-flow${selected.has(f.id) ? " is-selected" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(f.id)}
                        onChange={() => toggle(f.id)}
                      />
                      <span className="qa-rt-flow-name">{f.name}</span>
                      {(f.tags || []).map((t) => (
                        <span key={t} className="qa-tag">
                          {t}
                        </span>
                      ))}
                      {(f.sideEffects || []).map((t) => (
                        <span key={t} className="qa-tag qa-tag-warn">
                          {t}
                        </span>
                      ))}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
